import type { BaseFreightGroup } from '@api/useFreight/types'
import { BBDTag, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { MaterialIcon } from '@gravitate-js/excalibrr'
import { UpdatedTag } from '../../../../../shared/UpdatedTag'
import type { BaseFreightRate } from '@api/useFreight/types'
import { defaultDateDef } from '../../../../../../_SiteWide/CellTemplates/typeColumnDefs'
import { CalendarOutlined } from '@ant-design/icons'

interface UseBaseFreightColumnsProps {
  openEditDrawer: (group: BaseFreightGroup) => void
}

export function useBaseFreightColumns({ openEditDrawer }: UseBaseFreightColumnsProps) {
  const columnDefs = useMemo<ColDef<BaseFreightGroup>[]>(
    () => [
      {
        field: 'market',
        headerName: 'Region',
        flex: 1,
        valueGetter: (params: ValueGetterParams<BaseFreightGroup>) => {
          return params.data?.freight_region_name || params.data?.market || null
        },
        cellRenderer: RegionRenderer,
      },
      {
        headerName: 'Product Group',
        field: 'product_group',
        cellRenderer: ProductGroupRenderer,
      },
      {
        headerName: 'Updated',
        field: 'rows',
        filter: 'agDateColumnFilter',
        sortable: true,
        width: 140,
        minWidth: 140,
        valueGetter: (params: ValueGetterParams<BaseFreightGroup>) => {
          return findLatestRate(params.data?.rows || [])?.updated_on || null
        },
        filterParams: {
          browserDatePicker: true,
          comparator: (filterDate: Date, cellValue: Date) => {
            const filterDay = dayjs(filterDate).startOf('day')
            const cellDay = dayjs(cellValue).startOf('day')
            return cellDay.diff(filterDay, 'day')
          },
        },
        cellRenderer: UpdatedRenderer,
      },
      {
        headerName: 'Effective From',
        width: 120,
        minWidth: 120,
        filter: 'agDateColumnFilter',
        valueGetter: (params: ValueGetterParams<BaseFreightGroup>) => {
          return findActiveRate(params.data?.rows || [])?.effective_from || null
        },
        filterParams: {
          browserDatePicker: true,
          minValidYear: 2000,
          maxValidYear: 2100,
          comparator: (filterDate: Date, cellValue: Date) => {
            const filterDay = dayjs(filterDate).startOf('day')
            const cellDay = dayjs(cellValue).startOf('day')
            return cellDay.diff(filterDay, 'day')
          },
        },
        cellRenderer: EffectiveFromRenderer,
      },
      {
        headerName: 'To',
        filter: 'agDateColumnFilter',
        valueGetter: (params: ValueGetterParams<BaseFreightGroup>) => {
          return findActiveRate(params.data?.rows || [])?.effective_to || null
        },
        filterParams: {
          browserDatePicker: true,
          comparator: (filterDate: Date, cellValue: Date) => {
            const filterDay = dayjs(filterDate).startOf('day')
            const cellDay = dayjs(cellValue).startOf('day')
            return cellDay.diff(filterDay, 'day')
          },
        },
        cellRenderer: EffectiveToRenderer,
      },
      {
        headerName: 'Next Rate',
        ...defaultDateDef('next_scheduled_rate'),
        cellRenderer: NextRateRenderer,
      },
      {
        headerName: 'Actions',
        field: 'key',
        maxWidth: 100,
        minWidth: 100,
        sortable: false,
        pinned: 'right',
        filter: false,
        cellRenderer: (params: ICellRendererParams<BaseFreightGroup>) => (
          <ActionsRenderer params={params} onEdit={openEditDrawer} />
        ),
      },
    ],
    [openEditDrawer]
  )

  return columnDefs
}

// Helper Functions
function findActiveRate(rates: BaseFreightRate[]): BaseFreightRate | null {
  if (!rates.length) return null
  return rates.find((rate) => rate.status === 'Active') || rates[0]
}

function findLatestRate(rates: BaseFreightRate[]): BaseFreightRate | null {
  if (!rates.length) return null
  return [...rates].sort((a, b) => dayjs(b.updated_on).valueOf() - dayjs(a.updated_on).valueOf())[0]
}

// Renderer Components
function RegionRenderer({ value }: ICellRendererParams<string | null>) {
  return value ? <Texto>{value}</Texto> : <Texto appearance='hint'>Any</Texto>
}

function ProductGroupRenderer({ value }: ICellRendererParams<string | null>) {
  return value ? <BBDTag theme2>{value}</BBDTag> : <Texto appearance='hint'>Any</Texto>
}

function UpdatedRenderer({ data }: ICellRendererParams<BaseFreightGroup>) {
  const rows = data?.rows || []
  if (rows.length === 0) return null

  const sortedRows = [...rows].sort((a, b) => {
    return dayjs(b.updated_on).valueOf() - dayjs(a.updated_on).valueOf()
  })
  const mostRecentRow = sortedRows[0]
  const oldestRow = sortedRows[sortedRows.length - 1]

  if (!mostRecentRow) return <Texto appearance='hint'>-</Texto>

  return (
    <UpdatedTag
      created={{
        by: oldestRow.updated_by || 'System ',
        date: dayjs.utc(oldestRow.updated_on).local().format(),
      }}
      updated={{
        by: mostRecentRow.updated_by || 'System',
        date: dayjs.utc(mostRecentRow.updated_on).local().format(),
      }}
      title='Table Updated'
    />
  )
}

function EffectiveFromRenderer({ value }: ICellRendererParams<Date | null>) {
  if (!value) return <Texto appearance='hint'>-</Texto>
  return <Texto>{dayjs(value).format('MM/DD/YY')}</Texto>
}

function EffectiveToRenderer({ value }: ICellRendererParams<Date | null>) {
  if (!value) return <Texto appearance='hint'>Indefinite</Texto>
  return <Texto>{dayjs(value).format('MM/DD/YY')}</Texto>
}

function NextRateRenderer({ value }: ICellRendererParams<Date | null>) {
  if (!value) return <Texto appearance='hint'>N/A</Texto>
  return (
    <Horizontal gap={8} verticalCenter>
      <CalendarOutlined />
      <Texto>{dayjs(value).format('MM/DD/YY')}</Texto>
    </Horizontal>
  )
}

interface ActionsRendererProps {
  params: ICellRendererParams<BaseFreightGroup>
  onEdit: (group: BaseFreightGroup) => void
}

function ActionsRenderer({ params, onEdit }: ActionsRendererProps) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
      <Tooltip title='Edit'>
        <GraviButton
          icon={<MaterialIcon size={16} className='pt-1' icon='edit' />}
          onClick={(e) => {
            e.stopPropagation()
            if (params.data) {
              onEdit(params.data)
            }
          }}
        />
      </Tooltip>
    </div>
  )
}
